/* ForProfessionals — sección B2B. Conversión para el lado profesional.
   Visualmente cortada del resto: fondo azul oscuro, tipografía más sobria,
   sensación de "esta parte es para mí" para quien paga la suscripción. */

.section {
  position: relative;
  isolation: isolate;
  background: var(--blue-50);
  color: var(--color-text);
  padding-block: var(--space-20);
  overflow: hidden;
}

@media (min-width: 768px) {
  .section {
    padding-block: var(--space-24);
  }
}

@media (min-width: 1024px) {
  .section {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.inner {
  position: relative;
}

/* ─── Header ─── */

.header {
  text-align: center;
  max-width: 720px;
  margin: 0 auto var(--space-12);
}

@media (min-width: 768px) {
  .header {
    margin-bottom: var(--space-16);
  }
}

.eyebrow {
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin: 0 0 var(--space-3);
}

.title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: var(--color-text);
  margin: 0 0 var(--space-4);
}

.subtitle {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: var(--text-lg);
  line-height: 1.55;
  color: var(--color-text-muted);
  margin: 0;
}

/* ─── Grid de beneficios — 3 columnas desktop, stack mobile ─── */

.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
  margin-bottom: var(--space-12);
}

@media (min-width: 768px) {
  .grid {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-6);
    margin-bottom: var(--space-16);
  }
}

@media (min-width: 1024px) {
  .grid {
    gap: var(--space-8);
  }
}

/* ─── Card de beneficio ─── */

.card {
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  box-shadow: var(--shadow-sm);

  /* Reveal — scale-up 0.95 → 1, stagger via --i */
  opacity: 0;
  transform: translateY(16px) scale(0.95);
  transition:
    opacity 600ms var(--ease-decelerate),
    transform 600ms var(--ease-decelerate),
    box-shadow var(--duration-base) var(--ease-standard),
    border-color var(--duration-base) var(--ease-standard);
  transition-delay: calc(var(--i, 0) * 120ms);
  will-change: transform, opacity;
}

@media (min-width: 768px) {
  .card {
    padding: var(--space-8);
  }
}

.revealed .card {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.revealed .card:hover,
.revealed .card:focus-within {
  border-color: var(--color-border);
  box-shadow: var(--shadow-lg);
  transform: translateY(-4px) scale(1);
  transition-delay: 0ms;
}

.iconWrap {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-full);
  background: var(--jade-50);
  color: var(--jade-600);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-1);
  transition: transform var(--duration-base) var(--ease-standard);
}

.revealed .card:hover .iconWrap,
.revealed .card:focus-within .iconWrap {
  transform: rotate(6deg) scale(1.08);
}

.cardTitle {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-xl);
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--color-text);
  margin: 0;
}

.cardBody {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: var(--text-base);
  line-height: 1.6;
  color: var(--color-text-muted);
  margin: 0;
}

/* ─── Caja de precio — el ancla visual de la sección ─── */

.priceBox {
  position: relative;
  max-width: 480px;
  margin: 0 auto;
  background: var(--color-surface);
  color: var(--color-text);
  border-radius: var(--radius-xl);
  padding: var(--space-8) var(--space-6);
  text-align: center;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--color-border-subtle);

  /* Entrada más marcada: scale 0.92 → 1 con bounce sutil */
  opacity: 0;
  transform: scale(0.92);
  transition:
    opacity 600ms var(--ease-decelerate),
    transform 700ms cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
}

@media (min-width: 768px) {
  .priceBox {
    padding: var(--space-10) var(--space-8);
  }
}

.priceRevealed {
  opacity: 1;
  transform: scale(1);
}

.priceLabel {
  font-family: var(--font-body);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  margin: 0 0 var(--space-4);
}

.priceLine {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: var(--space-2);
  margin: 0 0 var(--space-3);
}

.priceAmount {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: clamp(26px, 3.4vw, 34px);
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--color-text);
}

.pricePeriod {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: var(--text-base);
  color: var(--color-text-muted);
}

.pricePromo {
  display: inline-block;
  font-family: var(--font-body);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--jade-700);
  background: var(--jade-50);
  padding: 6px 12px;
  border-radius: var(--radius-full);
  margin: 0 0 var(--space-4);
}

.priceGuarantee {
  font-family: var(--font-body);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-text-muted);
  margin: 0 0 var(--space-6);
}

/* ─── CTA pulsante ─── */

.cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-family: var(--font-body);
  font-weight: 600;
  font-size: var(--text-base);
  text-decoration: none;
  padding: 16px 28px;
  border-radius: var(--radius-full);
  min-height: 52px;
  box-shadow: var(--shadow-md);
  transition:
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard),
    background-color var(--duration-base) var(--ease-standard);
}

.cta:hover {
  background: var(--color-primary-hover);
  box-shadow: var(--shadow-lg);
  transform: translateY(-1px);
}

.cta:active {
  transform: scale(0.98);
}

.cta svg {
  transition: transform var(--duration-base) var(--ease-standard);
}

.cta:hover svg {
  transform: translateX(3px);
}

/* Pulse: corre 4 veces y se detiene. Solo arranca cuando la caja entró en vista
   para no llamar atención antes de que el usuario lea el contexto. */
@keyframes ctaPulse {
  0%,
  100% {
    box-shadow: var(--shadow-md), 0 0 0 0 rgba(46, 108, 200, 0.45);
  }
  50% {
    box-shadow: var(--shadow-md), 0 0 0 14px rgba(46, 108, 200, 0);
  }
}

.ctaPulse {
  animation: ctaPulse 2.4s var(--ease-standard) 0.6s 4;
}

/* Si el usuario interactúa, cortamos el pulse (no es ruido en hover) */
.ctaPulse:hover,
.ctaPulse:focus-visible {
  animation: none;
}
